import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { User } from 'lucide-react';

const STUDENT_LIST = [
  'Alice Johnson',
  'Bob Smith',
  'Charlie Davis',
  'Diana Prince',
  'Ethan Hunt',
  'Fiona Gallagher',
  'George Martin',
  'Hannah Lee',
  'Ian Wright',
  'Julia Roberts',
  'Kevin Brown',
  'Laura Wilson',
];

const ROTATION_CYCLE = 5 * 60 * 1000;

export default function AttendancePage() {
  const { attendanceId } = useParams<{ attendanceId: string }>();

  const [present, setPresent] = useState<boolean[]>(
    Array(STUDENT_LIST.length).fill(false)
  );
  const [lockedIndex, setLockedIndex] = useState<number | null>(null);
  const lockTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [rotationToken, setRotationToken] = useState<number>(
    Math.floor(Date.now() / ROTATION_CYCLE)
  );
  const [countdown, setCountdown] = useState<number>(
    (rotationToken + 1) * ROTATION_CYCLE - Date.now()
  );

  useEffect(() => {
    const updateRotation = () => {
      setRotationToken(Math.floor(Date.now() / ROTATION_CYCLE));
    };
    updateRotation();
    const timer = setInterval(updateRotation, ROTATION_CYCLE);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const updateCountdown = () => {
      const now = Date.now();
      const next = (rotationToken + 1) * ROTATION_CYCLE;
      setCountdown(next - now);
    };
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [rotationToken]);

  if (!attendanceId) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4">
        <h1 className="text-2xl font-bold mb-2">Invalid attendance link</h1>
        <p>Please check the URL and try again.</p>
      </div>
    );
  }

  const origin = window.location.origin;
  const checkInUrl =
    `${origin}/attendance/${attendanceId}/check-in?token=${rotationToken}`;
  const formatCountdown = () => {
    const m = Math.floor(countdown / 60000);
    const s = Math.floor((countdown % 60000) / 1000);
    return `${m.toString().padStart(2, '0')}:${s
      .toString()
      .padStart(2, '0')}`;
  };
  const togglePresence = (index: number) => {
    setPresent((prev) => {
      const next = [...prev];
      if (!prev[index]) {
        next[index] = true;
        setLockedIndex(index);
        if (lockTimer.current) {
          clearTimeout(lockTimer.current);
        }
        lockTimer.current = setTimeout(() => {
          setLockedIndex(null);
          lockTimer.current = null;
        }, 2000);
      } else {
        next[index] = false;
        setLockedIndex(null);
        if (lockTimer.current) {
          clearTimeout(lockTimer.current);
          lockTimer.current = null;
        }
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto w-full space-y-6">
        <div className="text-center">
          <h1 className="text-4xl font-bold">Attendance</h1>
          <p className="text-gray-600 mt-2">Session ID: {attendanceId}</p>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8 space-y-6 lg:space-y-0">
          <div className="lg:w-1/3 bg-white p-6 rounded-lg shadow">
            <h2 className="text-2xl font-semibold mb-4 text-center">
              Check-In QR Code
            </h2>
            <div className="flex justify-center mb-4">
              <QRCode value={checkInUrl} size={200} />
            </div>
            <p className="text-center text-gray-500 mb-2">Scan or go to:</p>
            <p className="text-center text-sm text-blue-600 break-all underline mb-4">
              {checkInUrl}
            </p>
            <p className="text-center text-gray-500">
              Refreshes in {formatCountdown()}
            </p>
          </div>
          <div className="flex-1 bg-white p-6 rounded-lg shadow flex flex-col">
            <h2 className="text-2xl font-semibold mb-4 text-center lg:text-left">
              Students
            </h2>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 overflow-y-auto"
              style={{ maxHeight: '60vh' }}
            >
              {STUDENT_LIST.map((name, idx) => (
                <div
                  key={idx}
                  className="flex items-center p-4 border rounded-lg bg-white"
                >
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-400" />
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="font-medium group">
                      {(() => {
                        const [firstName, lastName] = name.split(' ');
                        const surnameInitial = lastName?.charAt(0) ?? '';
                        const surnameRest = lastName?.slice(1) ?? '';
                        return (
                          <>
                            {firstName}&nbsp;
                            <span>{surnameInitial}</span>
                            <span
                              className="filter blur-sm group-hover:blur-none transition duration-200"
                              title={lastName}
                            >
                              {surnameRest}
                            </span>
                          </>
                        );
                      })()}
                    </p>
                  </div>
                  <button
                    disabled={lockedIndex !== null && idx !== lockedIndex}
                    onClick={() => togglePresence(idx)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium border ${
                      present[idx]
                        ? 'bg-green-100 border-green-500 text-green-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    } ${
                      lockedIndex !== null && idx !== lockedIndex
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    {present[idx] ? '✓ Here' : "I'm Here"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
